import Button from '@components/v2/ui/Button/Button';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { TabLabel } from '../tabs.types';
import { getTabSecondaryLabelTestId } from '../tabs.utils';

interface TabPillProps {
  label: TabLabel;
  secondaryLabel?: string | number;
  isActive?: boolean;
  maxWidth?: number;
  endIcon?: ReactNode;
  tooltip?: ReactNode;
  onClick?: () => void;
  testId?: string;
}

export function TabPill({
  label,
  secondaryLabel,
  isActive,
  maxWidth,
  endIcon,
  tooltip,
  onClick,
  testId,
}: TabPillProps) {
  const { translate, formatNumber } = useTranslation();

  return (
    <Box sx={{ display: 'inline-flex', flexShrink: 0, maxWidth }}>
      <Tooltip interactive placement="bottom" tabIndex={-1} TooltipContent={tooltip}>
        <Button
          variant="contained"
          color="secondary"
          shape="round"
          role="tab"
          aria-selected={isActive}
          tabIndex={0}
          isActive={isActive}
          endIcon={endIcon}
          onClick={onClick}
          testId={testId}
          sx={(theme) => ({
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.body2.fontWeight,
            color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
              '& .MuiTypography-micro1': { color: theme.palette.text.secondary },
            },
            ...(maxWidth !== undefined && { minWidth: 0, maxWidth: '100%' }),
          })}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'baseline',
              columnGap: 0.5,
              ...(maxWidth !== undefined && { minWidth: 0, whiteSpace: 'nowrap' }),
            }}
          >
            <Box
              component="span"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {translate(label as LangEnum)}
            </Box>
            {secondaryLabel !== undefined && (
              <Typography
                component="span"
                variant="micro1"
                sx={{ flexShrink: 0, color: isActive ? 'text.secondary' : 'text.tertiary' }}
                testId={testId ? getTabSecondaryLabelTestId(testId) : undefined}
              >
                {typeof secondaryLabel === 'number' ? formatNumber(secondaryLabel) : secondaryLabel}
              </Typography>
            )}
          </Box>
        </Button>
      </Tooltip>
    </Box>
  );
}
